import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  apiClient,
  setTokens,
  clearTokens,
  loadTokens,
  hasTokens,
  ApiRequestError,
} from '../api/client';

// ---- Types ----

export interface User {
  id: string;
  email: string;
  name: string;
  is_superadmin: boolean;
  email_verified: boolean;
  last_login: string | null;
  created_at: string;
}

export interface OrgMembership {
  org_id: string;
  org_name: string;
  org_slug: string;
  role: 'owner' | 'admin' | 'member';
}

interface AuthState {
  user: User | null;
  membership: OrgMembership | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface LoginPayload {
  email: string;
  password: string;
}

interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  org_name: string;
}

interface ForgotPasswordPayload {
  email: string;
}

interface AuthContextType extends AuthState {
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (payload: ForgotPasswordPayload) => Promise<void>;
  loadUser: () => Promise<void>;
}

// ---- Context ----

export const AuthContext = createContext<AuthContextType | null>(null);

// ---- Provider ----

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    membership: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Load existing session on mount
  useEffect(() => {
    loadTokens();
    if (hasTokens()) {
      fetchUser();
    } else {
      setState((s) => ({ ...s, isLoading: false }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchUser() {
    try {
      const [user, membership] = await Promise.all([
        apiClient.get<User>('/users/me'),
        apiClient.get<OrgMembership>('/users/me/membership'),
      ]);
      setState({
        user,
        membership,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) {
        clearTokens();
      }
      setState({
        user: null,
        membership: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }

  const login = useCallback(async (payload: LoginPayload) => {
    const tokens = await apiClient.post<{
      access_token: string;
      refresh_token: string;
    }>('/auth/login', payload, { skipAuth: true });
    setTokens(tokens);
    await fetchUser();
  }, []);

  const register = useCallback(async (payload: RegisterPayload) => {
    const tokens = await apiClient.post<{
      access_token: string;
      refresh_token: string;
    }>('/auth/register', payload, { skipAuth: true });
    setTokens(tokens);
    await fetchUser();
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore — we clear locally anyway
    }
    clearTokens();
    setState({
      user: null,
      membership: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  const forgotPassword = useCallback(
    async (payload: ForgotPasswordPayload) => {
      await apiClient.post('/auth/forgot-password', payload, {
        skipAuth: true,
      });
    },
    []
  );

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      login,
      register,
      logout,
      forgotPassword,
      loadUser: fetchUser,
    }),
    [state, login, register, logout, forgotPassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
